import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SystemError } from 'platform/Error';
import { globalTrackEvent } from 'platform/observability';
import { useHydrated } from 'platform/router/utils/useHydrated';
import { IS_PROD } from 'shared/helpers/env';
import {
  backendRefreshBlockDurationMS,
  offerPollingTimeoutThreshold,
} from 'shared/helpers/globals';
import { useIsomorphicLayoutEffect } from 'shared/hooks/useIsomorphicLayoutEffect';

type LoaderEntry = {
  label?: string;
  text?: string;
  startedAt: number;
};

const LOADER_STUCK_WARNING_MULTIPLIER = 2;
export const LOADER_STUCK_WARNING_MS =
  Math.max(backendRefreshBlockDurationMS, offerPollingTimeoutThreshold) *
  LOADER_STUCK_WARNING_MULTIPLIER;

type RouteContentLocation = {
  hash: string;
  key?: string;
  pathname: string;
  search: string;
};

type LoaderContextValue = {
  entries: Map<string, LoaderEntry>;
  routeContentCommitKey: string | null;
  markRouteContentCommitted: (key: string) => void;
  register: (key: string, entry: { label?: string; text?: string }) => void;
  unregister: (key: string) => void;
};

export function getRouteContentCommitKey(location: RouteContentLocation): string {
  return [location.key ?? '', location.pathname, location.search, location.hash].join('|');
}

const LoaderContext = createContext<LoaderContextValue | null>(null);

export const useLoaderContext = (): LoaderContextValue => {
  const value = useContext(LoaderContext);
  if (!value) {
    throw new SystemError('useLoaderContext must be used within a LoaderProvider');
  }
  return value;
};

type LoaderProviderProps = PropsWithChildren<{
  stuckWarningMs?: number;
}>;

export const LoaderProvider: FC<LoaderProviderProps> = ({
  children,
  stuckWarningMs = LOADER_STUCK_WARNING_MS,
}) => {
  const [entries, setEntries] = useState<Map<string, LoaderEntry>>(() => new Map());
  const [routeContentCommitKey, setRouteContentCommitKey] = useState<string | null>(null);
  const reportedStuckKeysRef = useRef<Set<string>>(new Set());

  const markRouteContentCommitted = useCallback((key: string) => {
    setRouteContentCommitKey(key);
  }, []);

  const register = useCallback<LoaderContextValue['register']>((key, entry) => {
    reportedStuckKeysRef.current.delete(key);
    setEntries((prev) => {
      const next = new Map(prev);
      next.set(key, { ...entry, startedAt: performance.now() });
      return next;
    });
  }, []);

  const unregister = useCallback<LoaderContextValue['unregister']>((key) => {
    reportedStuckKeysRef.current.delete(key);
    let entryToEmit: LoaderEntry | undefined;
    setEntries((prev) => {
      const existing = prev.get(key);
      if (!existing) {
        return prev;
      }
      entryToEmit = existing;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
    if (entryToEmit) {
      emitLoaderCompleted(key, entryToEmit);
    }
  }, []);

  const value = useMemo<LoaderContextValue>(
    () => ({
      entries,
      routeContentCommitKey,
      markRouteContentCommitted,
      register,
      unregister,
    }),
    [entries, markRouteContentCommitted, register, routeContentCommitKey, unregister],
  );

  useEffect(() => {
    if (entries.size === 0) {
      return undefined;
    }
    const timers = Array.from(entries.entries())
      .filter(([key]) => !reportedStuckKeysRef.current.has(key))
      .map(([key, entry]) => {
        const remainingMs = Math.max(0, stuckWarningMs - (performance.now() - entry.startedAt));
        return window.setTimeout(() => {
          reportedStuckKeysRef.current.add(key);
          emitLoaderStuck(key, entry);
        }, remainingMs);
      });
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
    };
  }, [entries, stuckWarningMs]);

  return <LoaderContext.Provider value={value}>{children}</LoaderContext.Provider>;
};

/**
 * Register a loading source for the duration of `isLoading` being true.
 * Use a stable, scope-prefixed `key` (e.g. `auth`, `seller-lead`, `route:home-details`).
 */
export const useTrackLoader = (
  key: string,
  isLoading: boolean,
  options?: { label?: string; text?: string },
): void => {
  const { register, unregister } = useLoaderContext();
  const hydrated = useHydrated();

  // Capture options at register time; text-only changes should not emit
  // `loader.completed`.
  useIsomorphicLayoutEffect(() => {
    // Skip the first hydration render so Suspense fallbacks do not update
    // their hydrating boundary.
    if (!hydrated || !isLoading) {
      return undefined;
    }
    register(key, { label: options?.label, text: options?.text });
    return () => unregister(key);
  }, [hydrated, key, isLoading, register, unregister]);
};

type SuspenseLoaderTrackerProps = {
  /**
   * Stable, scope-prefixed key for this Suspense fallback
   * (e.g. `suspense:auth-layout-chunk`). Surfaces in the
   * `loader.completed` event.
   */
  name: string;
  text?: string;
};

/**
 * Registers a Suspense fallback as a loading source while mounted.
 */
export const SuspenseLoaderTracker: FC<SuspenseLoaderTrackerProps> = ({ name, text }) => {
  useTrackLoader(name, true, { label: 'suspense', text });
  return null;
};

function emitLoaderCompleted(key: string, entry: LoaderEntry): void {
  globalTrackEvent('loader.completed', entry.label, {
    data: {
      key,
      duration_ms: performance.now() - entry.startedAt,
    },
  });
}

function emitLoaderStuck(key: string, entry: LoaderEntry): void {
  const durationMs = performance.now() - entry.startedAt;
  if (!IS_PROD) {
    // eslint-disable-next-line no-console -- dev/staging diagnostic for stuck root-loader gates
    console.warn('[loader] stuck', key, `${durationMs.toFixed(0)}ms`, {
      label: entry.label,
      text: entry.text,
    });
  }
  globalTrackEvent('loader.stuck', entry.label, {
    data: {
      key,
      duration_ms: durationMs,
    },
  });
}
